/**
 * ResolveRowModal — resolve one unmatched sheet row to a person.
 *
 * Search-first (existing GET /api/persons/search/), create only when the
 * search yields nothing — creation always stays behind a name check
 * (contract: "Person creation is not this endpoint's job"). Mirrors the
 * PersonList PersonModal pattern (shared Modal + debounced search) rather
 * than the Edit PersonModal, which is bound to the face-labeling flow.
 *
 * - The parsed name prefills the search query.
 * - Side comes from the reviewer (segmented picker labeled with the match's
 *   teams — the side's team context); when left unset the server falls back
 *   to the scan's stored roster and 400s if the person isn't in it.
 * - The sheet's jersey number is shown as a VERIFICATION hint only — it is
 *   never written to the person here (contract invariant 6). Creation adds
 *   the side's team membership with NO jersey number.
 *
 * On success: POST .../lineup-scan/resolve-row/ and hand the updated scan
 * resource (the GET shape) back to the parent.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  addTeamMember,
  createPerson,
  resolveLineupRow,
  searchPersons,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import styles from './LineupTab.module.css';

/** "Jo van der Berg" → { first_name: "Jo", last_name: "van der Berg" }. */
function splitName(name) {
  const parts = String(name || '').trim().split(/\s+/);
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' '),
  };
}

export default function ResolveRowModal({ match, row, rowIndex, onResolved, onClose }) {
  const parsed = row?.parsed || {};
  const [phase, setPhase] = useState('search'); // 'search' | 'create'
  const [query, setQuery] = useState(parsed.name || '');
  const [results, setResults] = useState([]);
  const [side, setSide] = useState(null); // null | 'home' | 'away'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(() => ({
    display_name: parsed.name || '',
    ...splitName(parsed.name),
  }));

  const jerseyHint = parsed.jersey != null ? parsed.jersey : null;

  // Debounced person search (PersonList precedent, scoped to this match's
  // roster context server-side via match_id).
  useEffect(() => {
    if (phase !== 'search' || !query || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await searchPersons(query.trim(), match.id);
        setResults(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error('Person search failed:', err);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [phase, query, match.id]);

  const resolveWith = async (personId) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await resolveLineupRow(match.id, rowIndex, personId, side);
      onResolved(updated);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You don’t have permission to resolve lineup rows.');
      } else {
        setError(err.response?.data?.error || 'Could not resolve this row.');
      }
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    const name = form.display_name.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const person = await createPerson({
        display_name: name,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
      // Side's team context — membership WITHOUT a jersey number (the sheet
      // number is verification data, applying it is a separate editor action).
      const teamId = side === 'home' ? match.home_team : side === 'away' ? match.away_team : null;
      if (teamId) {
        try {
          await addTeamMember(teamId, person.id, null);
        } catch (memberErr) {
          console.error('Team membership add failed (person created):', memberErr);
        }
      }
      const updated = await resolveLineupRow(match.id, rowIndex, person.id, side);
      onResolved(updated);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You don’t have permission to resolve lineup rows.');
      } else {
        setError(err.response?.data?.error || 'Could not create and resolve.');
      }
      setBusy(false);
    }
  };

  const sidePicker = (
    <div className={styles.formGroup}>
      <label className={styles.label}>Side</label>
      <div className={styles.sidePicker}>
        <button
          type="button"
          className={clsx(styles.sideBtn, side === 'home' && styles.sideBtnActive)}
          onClick={() => setSide(side === 'home' ? null : 'home')}
        >
          {match.home_team_name || 'Home'}
        </button>
        <button
          type="button"
          className={clsx(styles.sideBtn, side === 'away' && styles.sideBtnActive)}
          onClick={() => setSide(side === 'away' ? null : 'away')}
        >
          {match.away_team_name || 'Away'}
        </button>
      </div>
      <span className={styles.fieldHint}>
        Optional for players already in the scanned roster — required otherwise.
      </span>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Header onClose={onClose}>Resolve sheet row</Modal.Header>
      <Modal.Body>
        <p className={styles.resolveContext}>
          Sheet line: <span className={styles.resolveRaw}>{parsed.raw || parsed.name || '—'}</span>
        </p>
        {jerseyHint != null && (
          <p className={styles.jerseyHint}>
            Sheet says #{jerseyHint} — verification only, never written automatically.
          </p>
        )}
        {error && <p className={styles.error}>{error}</p>}

        {sidePicker}

        {phase === 'search' && (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>Search players</label>
              <input
                type="text"
                className={styles.input}
                value={query}
                autoFocus
                autoComplete="off"
                placeholder="Type a name…"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {results.length > 0 && (
              <div className={styles.resultsList}>
                {results.slice(0, 10).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={styles.resultItem}
                    disabled={busy}
                    onClick={() => resolveWith(p.id)}
                  >
                    <span className={styles.resultName}>
                      {p.display_name ||
                        `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                        `#${p.id}`}
                    </span>
                    {(p.team_memberships || []).map((m) => (
                      <span key={m.id || m.team_id} className={styles.resultTeamChip}>
                        {m.team_short_name || m.team_name}
                      </span>
                    ))}
                    {p.jersey_number != null && p.jersey_number !== '' && (
                      <span className={styles.resultJersey}>#{p.jersey_number}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {query.trim().length >= 2 && (
              <button
                type="button"
                className={styles.createNewRow}
                disabled={busy}
                onClick={() => {
                  setForm({
                    display_name: query.trim(),
                    ...splitName(query),
                  });
                  setPhase('create');
                }}
              >
                + Create new player &ldquo;{query.trim()}&rdquo;
              </button>
            )}
          </>
        )}

        {phase === 'create' && (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>Display name</label>
              <input
                type="text"
                className={styles.input}
                value={form.display_name}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>First name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.first_name}
                  autoComplete="off"
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Last name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.last_name}
                  autoComplete="off"
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </div>
            </div>
            <p className={styles.fieldHint}>
              The player joins {side ? (side === 'home' ? match.home_team_name : match.away_team_name) || 'the selected team' : 'no team yet (pick a side above)'} — without a jersey number.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {phase === 'create' && (
          <Button variant="ghost" onClick={() => setPhase('search')} disabled={busy}>
            Back to search
          </Button>
        )}
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {phase === 'create' && (
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={busy || !form.display_name.trim()}
          >
            {busy ? 'Resolving…' : 'Create & resolve'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
